/**
 * Client chung cho lượt chạy AI (Conversation → Run → Event):
 *   start   POST /runs                      → { run_id, conversation_id } ngay
 *   events  GET  /runs/{id}/events?format=agui (SSE, tự nối lại bằng Last-Event-ID; ngắt kết nối không huỷ run)
 *   cancel  POST /runs/{id}/cancel          → dừng chủ động
 *   resume  POST /runs/{id}/resume          → trả lời điểm chờ duyệt (vd. confirm_write của trợ lý Action Plan)
 * Event trên đường truyền theo tên AG-UI; `toLegacyEvent` đổi về tên cũ cho các hook đang dùng (useAiChat…).
 */
import request from '@/services/base-service';
import type { InternalAxiosRequestConfig } from 'axios';
import { AI_HUB_BASE } from '@/services/knowledge/ai-agent-api';
import { AiHubStreamError, open, readStream } from '@/services/knowledge/ai-agent-stream';
import type { AgentRunEvent, AiHubEnvelope, AiHubEvent } from '@/types/ai-agent';

const AUTH = { authorization: true, rejectOnError: true, silentError: true } as InternalAxiosRequestConfig;

export interface AgentRunBody {
  kind: 'chat' | 'step' | 'assistant';
  /** step: khoá sub-step (vd. "b2.swot") */
  agent_id?: string;
  /** chat: câu hỏi; step: yêu cầu thêm */
  input?: string;
  conversation_id?: string | null;
  scope_type?: string;
  scope_key?: string;
  page_context?: unknown;
  kb_ids?: string[];
  user_context?: Record<string, unknown>;
  snapshot?: Record<string, unknown>;
  context?: Record<string, unknown>;
  thinking?: boolean;
  threshold?: number;
  provider?: string;
  model?: string;
  api_key?: string | null;
  base_url?: string | null;
}

export interface AgentRunStart {
  run_id: string;
  conversation_id: string | null;
}

export interface EventsOptions {
  signal?: AbortSignal;
  lastEventId?: number;
  onEvent: (event: AgentRunEvent) => void;
}

const MAX_RECONNECTS = 5;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const unwrap = <T>(res: AiHubEnvelope<T>): T => {
  if (!res?.success) throw new AiHubStreamError(res?.message || 'Lỗi gọi trợ lý AI');
  return res.data;
};

export const agentRunClient = {
  start: async (body: AgentRunBody): Promise<AgentRunStart> =>
    unwrap(await (request.post(`${AI_HUB_BASE}/runs`, body, AUTH) as unknown as Promise<AiHubEnvelope<AgentRunStart>>)),

  /** Nghe một run tới khi `run.finished`; rơi mạng thì nối lại (tối đa 5 lần) từ event cuối đã nhận. */
  events: async (runId: string, options: EventsOptions): Promise<void> => {
    let lastId = options.lastEventId ?? 0;
    let finished = false;
    const onEvent = (event: AgentRunEvent) => {
      lastId = event.id || lastId;
      if (event.type === 'run.finished') finished = true;
      options.onEvent(event);
    };
    for (let attempt = 0; !finished; attempt += 1) {
      try {
        const res = await open('GET', `${AI_HUB_BASE}/runs/${runId}/events?format=agui`, undefined, options.signal, lastId);
        await readStream(res, onEvent as (e: AiHubEvent) => void);
        if (finished) return;
      } catch (error) {
        if (options.signal?.aborted || error instanceof AiHubStreamError || attempt >= MAX_RECONNECTS) throw error;
      }
      if (options.signal?.aborted) return;
      if (attempt >= MAX_RECONNECTS) throw new AiHubStreamError('Mất kết nối với trợ lý AI.');
      await sleep(1000 * (attempt + 1));
    }
  },

  cancel: async (runId: string) =>
    unwrap(await (request.post(`${AI_HUB_BASE}/runs/${runId}/cancel`, {}, AUTH) as unknown as Promise<AiHubEnvelope<unknown>>)),

  resume: async (
    runId: string,
    body: { interrupt_id: string; decision: 'approve' | 'reject' | 'edit'; payload?: Record<string, unknown> },
  ) =>
    unwrap(
      await (request.post(`${AI_HUB_BASE}/runs/${runId}/resume`, body, AUTH) as unknown as Promise<
        AiHubEnvelope<{ status: string; result?: { ok: boolean; done?: { id?: string }[]; error?: string } }>
      >),
    ),
};

/** Event AG-UI → tên event cũ (để các hook hiện có xử lý không đổi). */
export const toLegacyEvent = (event: AgentRunEvent): AiHubEvent => {
  const { id, type, data } = event;
  const d = data as Record<string, unknown>;
  const legacy = (t: string, payload: Record<string, unknown> = d): AiHubEvent =>
    ({ id, type: t, data: payload }) as AiHubEvent;
  switch (type) {
    case 'text.delta':
      return legacy('token', { text: String(d.delta ?? '') });
    case 'text.end':
      return legacy('message');
    case 'run.error':
      return legacy('error');
    case 'step.started':
      return legacy(d.source === 'child' ? 'child.started' : 'node.started');
    case 'step.finished':
      return legacy(d.source === 'child' ? 'child.finished' : 'node.finished');
    case 'tool.start':
      return legacy('tool.call');
    case 'data.hits':
      return legacy('kb.hits');
    case 'ui': {
      const component = String(d.component ?? '');
      const props = (d.props ?? {}) as Record<string, unknown>;
      return legacy(component === 'confirm_write' ? 'pending_action' : component, props);
    }
    case 'custom':
      return legacy(String(d.name ?? 'custom'), (d.value ?? {}) as Record<string, unknown>);
    default:
      return legacy(type);
  }
};

export interface StreamRunOptions {
  signal?: AbortSignal;
  onEvent: (event: AiHubEvent) => void;
  /** Có run_id ngay sau POST /runs — lưu để nối lại khi tải lại trang / để Dừng gọi cancel. */
  onRunStarted?: (start: AgentRunStart) => void;
}

/** Tạo run rồi nghe tới hết, phát event dạng cũ. Abort chỉ ngừng nghe; muốn dừng run gọi `agentRunClient.cancel`. */
export const streamRun = async (body: AgentRunBody, options: StreamRunOptions): Promise<AgentRunStart> => {
  const started = await agentRunClient.start(body);
  options.onRunStarted?.(started);
  await agentRunClient.events(started.run_id, {
    signal: options.signal,
    onEvent: (e) => options.onEvent(toLegacyEvent(e)),
  });
  return started;
};
